import { useMemo, useRef, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  commitPassSafeImport,
  previewPassSafeImport,
  type ImportDraft,
  type ImportMatchHit,
  type ImportPreview,
  type Option,
} from '../../api/settingsPassSafe'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'

/**
 * Import with AI: poza sau PDF → lista de confirmat → seif.
 *
 * Nimic nu se scrie pana apasa Confirm. Un aparat care nu e in inventar se
 * adauga numai daca omul a bifat "Create a new device" si a ales locatia.
 * Un nume apropiat, nu identic, se arata ca sugestie - nu se alege singur.
 */

type DraftRow = ImportDraft & {
  include: boolean
  office_id: number
  device_id: number
  discovered_id: number
}

function asRow(item: ImportDraft): DraftRow {
  const hit = item.match
  return {
    ...item,
    include: true,
    office_id: 0,
    device_id: hit?.source === 'inventory' ? hit.id : 0,
    discovered_id: hit?.source === 'discovered' ? hit.id : 0,
  }
}

function pick(row: DraftRow, hit: ImportMatchHit | null, action: DraftRow['action']): DraftRow {
  return {
    ...row,
    action,
    match: hit,
    device_id: hit?.source === 'inventory' ? hit.id : 0,
    discovered_id: hit?.source === 'discovered' ? hit.id : 0,
    device_type: hit && hit.type_id > 0 ? hit.type_id : row.device_type,
  }
}

export function ImportWithAiModal({
  open,
  onClose,
  onSaved,
}: {
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [rows, setRows] = useState<DraftRow[]>([])
  const [problem, setProblem] = useState('')
  const [busy, setBusy] = useState(false)

  const reset = () => {
    setFile(null)
    setPreview(null)
    setRows([])
    setProblem('')
    setBusy(false)
    if (input.current) input.current.value = ''
  }

  const close = () => {
    reset()
    onClose()
  }

  const selected = useMemo(() => rows.filter((row) => row.include), [rows])

  const read = async (chosen: File) => {
    setBusy(true)
    setProblem('')
    try {
      const answer = await previewPassSafeImport(chosen)
      setPreview(answer)
      setRows(answer.items.map(asRow))
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'The document could not be read.')
      setPreview(null)
      setRows([])
    } finally {
      setBusy(false)
    }
  }

  const save = async () => {
    if (selected.length === 0) {
      setProblem('Tick at least one row to import.')
      return
    }
    const missing = selected.find((row) => {
      if (row.password === '') return true
      if (row.device_type <= 0) return true
      if (row.action === 'create') return row.found_name === '' || row.office_id <= 0
      return row.device_id <= 0 && row.discovered_id <= 0
    })
    if (missing) {
      setProblem('Fill in the missing fields on the ticked rows (device, type, location, password).')
      return
    }
    setBusy(true)
    setProblem('')
    try {
      const answer = await commitPassSafeImport(
        selected.map((row) => ({
          include: true,
          action: row.action,
          found_name: row.found_name,
          ip: row.ip,
          device_id: row.device_id,
          discovered_id: row.discovered_id,
          device_type: row.device_type,
          office_id: row.office_id,
          role: row.role,
          user: row.user,
          password: row.password,
          description: row.description,
        })),
      )
      const bits = []
      if (answer.created_passwords > 0) {
        bits.push(answer.created_passwords === 1 ? '1 password' : `${answer.created_passwords} passwords`)
      }
      if (answer.created_devices > 0) {
        bits.push(answer.created_devices === 1 ? '1 new device' : `${answer.created_devices} new devices`)
      }
      toast(bits.length > 0 ? `Imported ${bits.join(' and ')}.` : 'Import saved.', 'Success', 'success')
      onSaved()
      close()
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : 'Nothing was saved.')
    } finally {
      setBusy(false)
    }
  }

  const patch = (key: string, next: Partial<DraftRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...next } : row)))
  }

  return (
    <Modal
      id="importWithAiModal"
      open={open}
      onClose={close}
      large
      staticBackdrop
      labelledBy="importWithAiModalLabel"
      title={
        <>
          <i className="fas fa-magic" aria-hidden="true"></i> Import with AI
        </>
      }
    >
      <div className="ps-import">
        {preview === null ? (
          <div className="ps-drop">
            <input
              ref={input}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
              hidden
              onChange={(event) => {
                const chosen = event.target.files?.[0] ?? null
                setFile(chosen)
                if (chosen) void read(chosen)
              }}
            />
            <p>
              Photo or PDF of a password list. The system reads the devices and passwords, then you confirm
              each one before anything is saved.
            </p>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? 'Reading…' : 'Choose file'}
            </button>
            {file ? <div className="ps-file-name">{file.name}</div> : null}
          </div>
        ) : (
          <div>
            <p className="ps-import-lead">
              Check every row. Existing devices that almost match the written name are offered as suggestions —
              they are not chosen for you. A device that is not in inventory is added only if you confirm it.
            </p>
            {rows.map((row) => (
              <ImportRow
                key={row.key}
                row={row}
                offices={preview.offices}
                types={preview.deviceTypes}
                onPatch={(next) => patch(row.key, next)}
                onPick={(hit, action) => setRows((prev) => prev.map((item) => (item.key === row.key ? pick(item, hit, action) : item)))}
              />
            ))}
          </div>
        )}
        {problem !== '' ? <div className="alert alert-danger ps-error">{problem}</div> : null}
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={close}>
          Close
        </button>
        {preview !== null ? (
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? 'Saving…' : `Confirm ${selected.length} selected`}
          </button>
        ) : null}
      </div>
    </Modal>
  )
}

function ImportRow({
  row,
  offices,
  types,
  onPatch,
  onPick,
}: {
  row: DraftRow
  offices: Option[]
  types: Option[]
  onPatch(next: Partial<DraftRow>): void
  onPick(hit: ImportMatchHit | null, action: DraftRow['action']): void
}) {
  return (
    <div className={`ps-imp-row${row.include ? '' : ' ps-imp-row-off'}`}>
      <label className="ps-imp-keep">
        <input type="checkbox" checked={row.include} onChange={(e) => onPatch({ include: e.target.checked })} />
        Import
      </label>
      <div className="ps-imp-body">
        <div className="ps-imp-head">
          <strong>{row.found_name || '(no name)'}</strong>
          {row.ip ? <span className="ps-imp-meta">{row.ip}</span> : null}
          {row.type_guess ? <span className="ps-imp-meta">{row.type_guess}</span> : null}
        </div>

        {row.action === 'existing' && row.match ? (
          <p className="ps-evidence">Matches existing {row.match.source === 'inventory' ? 'inventory' : 'discovered'} device: {row.match.name}{row.match.ip ? ` · ${row.match.ip}` : ''}.</p>
        ) : null}

        {row.action === 'suggest' ? (
          <div className="ps-imp-suggest">
            <p className="ps-evidence ps-evidence-none">
              The written name is close to a device already in the list, but not the same. Pick one, or create it.
            </p>
            {row.suggestions.map((hit) => (
              <button
                key={`${hit.source}-${hit.id}`}
                type="button"
                className={`ps-imp-choice${row.device_id === hit.id || row.discovered_id === hit.id ? ' on' : ''}`}
                onClick={() => onPick(hit, 'existing')}
              >
                {hit.name}
                {hit.ip ? ` · ${hit.ip}` : ''}
                <span className="ps-imp-meta"> {hit.source === 'inventory' ? 'Registered' : 'Discovered'}</span>
              </button>
            ))}
            {/* In ramura `suggest` actiunea nu poate fi `create` (tsc -b pica pe
                comparatie); butonul se aprinde in ramura `create` de mai jos. */}
            <button type="button" className="ps-imp-choice" onClick={() => onPick(null, 'create')}>
              Create a new device
            </button>
          </div>
        ) : null}

        {row.action === 'create' ? (
          <div className="ps-imp-create">
            <p className="ps-evidence ps-evidence-none">This device is not in inventory. Confirm the name, type and location to add it.</p>
            <div className="ps-role-fields">
              <div>
                <label>Name</label>
                <input className="form-control" value={row.found_name} onChange={(e) => onPatch({ found_name: e.target.value })} />
              </div>
              <div>
                <label>Location</label>
                <select className="form-control" value={row.office_id || ''} onChange={(e) => onPatch({ office_id: Number(e.target.value || 0) })}>
                  <option value="">Select..</option>
                  {offices.map((office) => (
                    <option key={office.id} value={office.id}>
                      {office.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ) : null}

        <div className="ps-role-fields">
          <div>
            <label>Device type</label>
            <select className="form-control" value={row.device_type || ''} onChange={(e) => onPatch({ device_type: Number(e.target.value || 0) })}>
              <option value="">Select..</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Account</label>
            <input className="form-control" value={row.user} onChange={(e) => onPatch({ user: e.target.value })} />
          </div>
          <div>
            <label>Password</label>
            <input className="form-control" type="password" autoComplete="new-password" value={row.password} onChange={(e) => onPatch({ password: e.target.value })} />
          </div>
          <div>
            <label>Description</label>
            <input className="form-control" value={row.description} onChange={(e) => onPatch({ description: e.target.value })} />
          </div>
        </div>
      </div>
    </div>
  )
}
